import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import Breadcrumbs from "../components/Breadcrumbs";
import CardTile from "../components/CardTile";
import Pagination from "../components/Pagination";
import ScrollToTopButton from "../components/ScrollToTopButton";
import { getSetBySlug, getSet, listCardsInSet } from "../lib/api";
import { setPath } from "../lib/slug";
import { setDetailMeta, breadcrumbList } from "../lib/seo";

/**
 * Sets-detail page under the canonical URL scheme
 * `/{game}/{lang}/sets/{setSlug}`.
 *
 * Resolution:
 *   1. Treat the URL segment as a set-slug and hit `getSetBySlug`.
 *   2. If that 404s, treat it as an `external_id` and hit `getSet`.
 *
 * Public-visibility gate on both endpoints preserves the W4 hide-list.
 *
 * SEO (2026-02-01):
 *   - <Helmet> for client-side head updates after SPA navigation.
 *   - JSON-LD BreadcrumbList (Game → English Sets → Set).
 *   - The initial document render already carries these tags via the
 *     backend meta-injection middleware.
 */
export default function SetDetailPage() {
  const { game, lang, setSlug } = useParams();
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [set, setSet] = useState(null);
  const [data, setData] = useState(null);
  const [state, setState] = useState("loading");
  const [error, setError] = useState(null);

  // ---- Resolve set (slug → external_id fallback + canonical replace) ----
  useEffect(() => {
    let alive = true;
    setState("loading");
    (async () => {
      let resolved = null;
      try {
        resolved = await getSetBySlug(setSlug);
      } catch (_slugErr) {
        try {
          resolved = await getSet(setSlug);
        } catch (extErr) {
          if (!alive) return;
          setError(extErr?.response?.data?.detail || "Set not found");
          setState("error");
          return;
        }
      }
      if (!alive) return;
      const canonical = setPath(game, lang, resolved.name || resolved.external_id);
      if (canonical !== `/${game}/${lang}/sets/${setSlug}`) {
        navigate(canonical, { replace: true });
        return;
      }
      setSet(resolved);
    })();
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [game, lang, setSlug]);

  // ---- Load cards once the set is resolved / paginated ----
  useEffect(() => {
    let alive = true;
    if (!set) return () => { alive = false; };
    setState("loading");
    listCardsInSet(set.external_id, { page, pageSize: 60 })
      .then((d) => {
        if (!alive) return;
        setData(d);
        setState("ready");
      })
      .catch((e) => {
        if (!alive) return;
        setError(e?.response?.data?.detail || e?.message || "Failed to load");
        setState("error");
      });
    return () => {
      alive = false;
    };
  }, [set, page]);

  const cards = data?.cards || [];
  const total = data?.total || 0;
  const totalPages = data ? Math.max(1, Math.ceil(data.total / data.page_size)) : 1;

  const meta = useMemo(() => (set ? setDetailMeta(set) : null), [set]);
  const jsonld = useMemo(() => {
    if (!set) return null;
    return breadcrumbList({
      items: [
        { name: "Pokémon", path: "/pokemon" },
        { name: "English Sets", path: "/pokemon/en/sets" },
        { name: set.name || setSlug, path: setPath(game, lang, set.name || set.external_id) },
      ],
    });
  }, [set, game, lang, setSlug]);

  return (
    <div
      className="mx-auto w-full max-w-[1400px] px-5 py-8 md:px-gutter md:py-10"
      data-testid="set-detail-page"
    >
      {meta && (
        <Helmet>
          <title>{meta.title}</title>
          <meta name="description" content={meta.description} />
          <meta name="robots" content={meta.robots} />
          <link rel="canonical" href={meta.canonical} />
          <meta property="og:site_name" content="CardCapture" />
          <meta property="og:title" content={meta.title} />
          <meta property="og:description" content={meta.description} />
          <meta property="og:url" content={meta.canonical} />
          <meta property="og:type" content={meta.ogType} />
          <meta property="og:image" content={meta.ogImage} />
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content={meta.title} />
          <meta name="twitter:description" content={meta.description} />
          <meta name="twitter:image" content={meta.ogImage} />
          {jsonld && (
            <script type="application/ld+json">
              {JSON.stringify(jsonld)}
            </script>
          )}
        </Helmet>
      )}

      <Breadcrumbs
        items={[
          { label: "Home", to: "/" },
          { label: "Pokémon", to: "/pokemon" },
          { label: "English", to: "/pokemon/en/sets" },
          { label: set?.name || setSlug },
        ]}
      />

      {state === "loading" && !data && (
        <div className="mt-16 text-body text-ink-muted" data-testid="set-loading">
          Loading set…
        </div>
      )}
      {state === "error" && (
        <div className="mt-16 text-body text-ink-muted" data-testid="set-error">
          {error}
        </div>
      )}

      {set && (
        <>
          <div className="mt-6 flex flex-col gap-2 md:flex-row md:items-end md:justify-between">
            <div>
              <div className="text-eyebrow uppercase text-ink-muted">
                {set.release_date
                  ? new Date(set.release_date + "T00:00:00Z").toLocaleDateString(
                      undefined,
                      { year: "numeric", month: "long", day: "numeric" },
                    )
                  : "release date unknown"}
              </div>
              <h1 className="mt-1 text-title-page text-ink-text">{set.name}</h1>
            </div>
            <div
              className="flex items-center gap-3 text-meta text-ink-muted tabular"
              data-testid="set-meta"
            >
              <span>{total} cards</span>
              <span aria-hidden className="text-ink-chip">·</span>
              <span>page {page} of {totalPages}</span>
            </div>
          </div>

          {cards.length === 0 ? (
            <div
              className="mt-12 rounded-card border border-ink-chip bg-ink-surface p-8 text-body text-ink-muted"
              data-testid="cards-empty"
            >
              This set has no imported cards yet. The catalog sync may still
              be running.
            </div>
          ) : (
            <div
              data-testid="cards-grid"
              className="mt-8 grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-6"
            >
              {cards.map((v) => (
                <CardTile key={v.id} variant={v} set={set} />
              ))}
            </div>
          )}

          {totalPages > 1 && (
            <Pagination
              page={page}
              totalPages={totalPages}
              onChange={(n) => setPage(n)}
              testId="set-detail-pagination"
            />
          )}
        </>
      )}
      <ScrollToTopButton />
    </div>
  );
}
